// React
import { useMemo, Fragment, useState } from "react"

// Dates
import { useLocalizedDateTime } from "@/lib/dates"

// Table
import {
	getCoreRowModel,
	useReactTable,
	createColumnHelper,
	flexRender,
	CellContext,
} from "@/lib/table"

// UI
import { CardBody, CardWrapper } from "@/components/Card"
import { Heading } from "@/components/Typography"
import {
	Table,
	TableBody,
	TableDataCell,
	TableRowCell,
	TableHead,
	TableHeading,
} from "@/components/table-controls/TableItems"
import { PaginationAsButtons } from "@/components/PaginationAsButtons"
import { ChevronDownIcon, ChevronRightIcon } from "@/lib/icons"

// Translations
import { useLang } from "@/context/lang"
import { useTrans } from "@/lib/i18n"

// Query
import {
	useProjectOwnersOverviewHeadingQuery,
	useProjectOwnersOverviewQuery,
	type ProjectOwnersOverviewQuery,
} from "@/api/graphql"

// Constants
import {
	ProjectCrowdfundingTypes,
	endDateStatistics,
} from "@/constants/constants"

// Components
import { ProjectsNestedTable } from "./ProjectsNestedTable"

// Types
import type { PublicProjectType } from "@/api/graphql/types"

// Types
type ProjectOwnerFromQuery = NonNullable<
	NonNullable<
		NonNullable<
			ProjectOwnersOverviewQuery["platform_statistics"]
		>["project_owners"]
	>["results"]
>[number]

type ProjectOwnerTableRow = NonNullable<ProjectOwnerFromQuery> & {
	subRows?: Array<PublicProjectType>
}

/**
 * ProjectOwnersOverview
 * Displays overview data for project owners and their projects
 */
export function ProjectOwnersOverview() {
	// Constants
	const projectsPerPage = 10

	// State
	const [expanded, setExpanded] = useState({})
	const [currentPage, setCurrentPage] = useState<number>(0)

	// Dates
	const DateTime = useLocalizedDateTime()

	// Translations
	const t = useTrans(["common", "dashboard"])
	const { formatNumber, formatCurrency } = useLang()

	// Query
	const { data: projectOwnersOverviewResults, isLoading: isLoadingTable } =
		useProjectOwnersOverviewQuery({
			project_types: ProjectCrowdfundingTypes,
			date: endDateStatistics,
			limit: projectsPerPage,
			offset: currentPage * projectsPerPage,
		})
	const { data: projectOwnersOverviewHeading, isLoading: isLoadingHeading } =
		useProjectOwnersOverviewHeadingQuery({
			project_types: ProjectCrowdfundingTypes,
			date: endDateStatistics,
		})

	// Transform query data to table format with nested structure
	const tableData = useMemo(() => {
		if (
			!projectOwnersOverviewResults?.platform_statistics?.project_owners
				?.results
		)
			return []

		return projectOwnersOverviewResults.platform_statistics.project_owners.results
			.filter(
				(owner): owner is NonNullable<typeof owner> => owner !== null,
			)
			.map((owner) => ({
				...owner,
				subRows: owner.projects?.filter(
					(project): project is NonNullable<typeof project> =>
						project !== null,
				) as PublicProjectType[],
			}))
	}, [projectOwnersOverviewResults])

	// Table setup
	const columnHelper = createColumnHelper<ProjectOwnerTableRow>()
	const columns = useMemo(
		() => [
			columnHelper.display({
				id: "expander",
				header: () => (
					<TableHeading variant="static" className="w-12" />
				),
				cell: (info: CellContext<ProjectOwnerTableRow, unknown>) => {
					const { row } = info
					if (
						!row.original.subRows ||
						row.original.subRows.length === 0
					) {
						return <TableDataCell className="w-12" />
					}
					const isRowExpanded =
						expanded[row.id as keyof typeof expanded]
					return (
						<TableDataCell className="w-12">
							<button
								onClick={() =>
									setExpanded((prev) => ({
										...prev,
										[row.id]:
											!prev[row.id as keyof typeof prev],
									}))
								}
								className="rounded p-1 hover:bg-gray-100"
								aria-label={
									isRowExpanded
										? t("common:common.collapse")
										: t("common:common.expand")
								}
							>
								{isRowExpanded ? (
									<ChevronDownIcon className="size-4" />
								) : (
									<ChevronRightIcon className="size-4" />
								)}
							</button>
						</TableDataCell>
					)
				},
			}),
			columnHelper.accessor("business_legal_name", {
				header: () => (
					<TableHeading variant="static">
						{t(
							"dashboard:dashboard.statistics.platform.business_legal_name",
						)}
					</TableHeading>
				),
				cell: (info: CellContext<ProjectOwnerTableRow, string>) => (
					<TableDataCell className="font-medium">
						{info.getValue()}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("business_nr", {
				header: () => (
					<TableHeading variant="static">
						{t(
							"dashboard:dashboard.statistics.platform.business_nr",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<ProjectOwnerTableRow, string | null>,
				) => (
					<TableDataCell>
						{info.getValue() ?? (
							<span className="text-gray-400">-</span>
						)}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("total_amount_raised", {
				header: () => (
					<TableHeading variant="static">
						{t(
							"dashboard:dashboard.statistics.platform.total_amount_raised_owner",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<ProjectOwnerTableRow, string | null>,
				) => {
					return (
						<TableDataCell>
							{info.getValue() ? (
								<>
									{formatCurrency(
										Number(info.getValue()) ?? 0,
									)}
								</>
							) : (
								<span className="text-gray-400">-</span>
							)}
						</TableDataCell>
					)
				},
			}),
			columnHelper.accessor("total_projects", {
				header: () => (
					<TableHeading variant="static">
						{t(
							"dashboard:dashboard.stats.platform_stats.blocks.total_projects",
						)}
					</TableHeading>
				),
				cell: (info: CellContext<ProjectOwnerTableRow, number>) => (
					<TableDataCell>
						{formatNumber(info.getValue() ?? 0)}
					</TableDataCell>
				),
			}),
		],
		[columnHelper, t, formatNumber, setExpanded, expanded],
	)

	const table = useReactTable({
		columns,
		data: tableData,
		getCoreRowModel: getCoreRowModel(),
	})

	return (
		<CardWrapper className="mb-4">
			<CardBody>
				<Heading as="h3" className="mb-6">
					{t(
						"dashboard:dashboard.statistics.platform.project_owners_title",
					)}
				</Heading>
				<p className="mb-4 text-sm text-gray-600">
					{t("dashboard:dashboard.statistics.platform.as_per_date", {
						date: DateTime.fromJSDate(
							endDateStatistics,
						).toLocaleString(),
					})}
				</p>

				{/* Summary Statistics */}
				{isLoadingHeading ? (
					<SummaryStatisticsLoadingState />
				) : (
					<div className="mb-6 grid grid-cols-1 gap-4 rounded-lg bg-gray-50 p-4 md:grid-cols-3">
						<div className="text-center">
							<div className="text-primary text-2xl font-bold">
								{formatNumber(
									projectOwnersOverviewHeading
										?.platform_statistics?.total_projects ??
										0,
								)}
							</div>
							<div className="text-sm text-gray-600">
								{t(
									"dashboard:dashboard.statistics.platform.total_crowdfunding_projects",
								)}
							</div>
						</div>
						<div className="text-center">
							<div className="text-primary text-2xl font-bold">
								{formatNumber(
									projectOwnersOverviewHeading
										?.platform_statistics?.project_owners
										?.total_count ?? 0,
								)}
							</div>
							<div className="text-sm text-gray-600">
								{t(
									"dashboard:dashboard.statistics.platform.total_project_owners",
								)}
							</div>
						</div>
						<div className="text-center">
							<div className="text-primary text-2xl font-bold">
								{formatNumber(
									projectOwnersOverviewHeading
										?.platform_statistics?.project_owners
										?.total_count_multiple_projects ?? 0,
								)}
							</div>
							<div className="text-sm text-gray-600">
								{t(
									"dashboard:dashboard.statistics.platform.total_project_owners_multiple",
								)}
							</div>
						</div>
					</div>
				)}

				{/* Table */}
				{isLoadingTable ? (
					<TableLoadingState />
				) : (
					<Table>
						<TableHead>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRowCell
									key={headerGroup.id}
									withHover={false}
								>
									{headerGroup.headers.map((header) => (
										<Fragment key={header.id}>
											{flexRender(
												header.column.columnDef.header,
												header.getContext(),
											)}
										</Fragment>
									))}
								</TableRowCell>
							))}
						</TableHead>
						<TableBody>
							{table.getRowModel().rows.map((row, index) => {
								const isExpanded =
									expanded[row.id as keyof typeof expanded]
								const hasProjects =
									row.original.subRows &&
									row.original.subRows.length > 0

								return (
									<Fragment key={row.id}>
										<TableRowCell
											isOdd={index % 2 === 0}
											withHover={true}
										>
											{row
												.getVisibleCells()
												.map((cell) => (
													<Fragment key={cell.id}>
														{flexRender(
															cell.column
																.columnDef.cell,
															cell.getContext(),
														)}
													</Fragment>
												))}
										</TableRowCell>
										{isExpanded && hasProjects && (
											<tr>
												<td colSpan={5} className="p-0">
													<ProjectsNestedTable
														projects={
															row.original
																.subRows!
														}
													/>
												</td>
											</tr>
										)}
									</Fragment>
								)
							})}
						</TableBody>
					</Table>
				)}

				<div className="mt-6 flex w-full justify-center px-4">
					<PaginationAsButtons
						countPerPage={projectsPerPage}
						totalCount={
							projectOwnersOverviewHeading?.platform_statistics
								?.project_owners?.total_count ?? 0
						}
						itemType={"common.pagination.item_types.project"}
						currentPage={currentPage + 1}
						currentItemsAmount={
							projectOwnersOverviewHeading?.platform_statistics
								?.project_owners?.total_count ?? 0
						}
						onNextPage={() =>
							setCurrentPage((currentPage) => currentPage + 1)
						}
						onPrevPage={() =>
							setCurrentPage((currentPage) =>
								Math.max(currentPage - 1, 0),
							)
						}
						analyticsId="project-owners-overview"
					/>
				</div>
			</CardBody>
		</CardWrapper>
	)
}

/**
 * SummaryStatisticsLoadingState
 * Loading state component for Summary Statistics section
 */
function SummaryStatisticsLoadingState() {
	// Translations
	const t = useTrans(["dashboard"])

	return (
		<div className="mb-6 grid grid-cols-1 gap-4 rounded-lg bg-gray-50 p-4 md:grid-cols-3">
			<div className="text-center">
				<div className="text-primary mb-1 text-2xl font-bold">
					<span className="inline-block h-8 w-20 animate-pulse rounded bg-gray-200" />
				</div>
				<div className="text-sm text-gray-600">
					{t(
						"dashboard:dashboard.statistics.platform.total_crowdfunding_projects",
					)}
				</div>
			</div>
			<div className="text-center">
				<div className="text-primary mb-1 text-2xl font-bold">
					<span className="inline-block h-8 w-20 animate-pulse rounded bg-gray-200" />
				</div>
				<div className="text-sm text-gray-600">
					{t(
						"dashboard:dashboard.statistics.platform.total_project_owners",
					)}
				</div>
			</div>
			<div className="text-center">
				<div className="text-primary mb-1 text-2xl font-bold">
					<span className="inline-block h-8 w-20 animate-pulse rounded bg-gray-200" />
				</div>
				<div className="text-sm text-gray-600">
					{t(
						"dashboard:dashboard.statistics.platform.total_project_owners_multiple",
					)}
				</div>
			</div>
		</div>
	)
}

/**
 * TableLoadingState
 * Loading state component for Table section
 */
function TableLoadingState() {
	// Translations
	const t = useTrans(["dashboard"])

	return (
		<Table>
			<TableHead>
				<TableRowCell withHover={false}>
					<TableHeading variant="static" className="w-12" />
					<TableHeading variant="static">
						{t(
							"dashboard:dashboard.statistics.platform.business_legal_name",
						)}
					</TableHeading>
					<TableHeading variant="static">
						{t(
							"dashboard:dashboard.statistics.platform.business_nr",
						)}
					</TableHeading>
					<TableHeading variant="static">
						{t(
							"dashboard:dashboard.statistics.platform.total_amount_raised_owner",
						)}
					</TableHeading>
					<TableHeading variant="static">
						{t(
							"dashboard:dashboard.stats.platform_stats.blocks.total_projects",
						)}
					</TableHeading>
				</TableRowCell>
			</TableHead>
			<TableBody>
				{/* Show 5 skeleton rows */}
				{[...Array(5)].map((_, index) => (
					<TableRowCell
						key={index}
						isOdd={index % 2 === 0}
						withHover={false}
					>
						<TableDataCell className="w-12">
							<div className="size-4" />
						</TableDataCell>
						<TableDataCell>
							<span className="inline-block h-4 w-48 animate-pulse rounded bg-gray-200" />
						</TableDataCell>
						<TableDataCell>
							<span className="inline-block h-4 w-20 animate-pulse rounded bg-gray-200" />
						</TableDataCell>
						<TableDataCell>
							<span className="inline-block h-4 w-24 animate-pulse rounded bg-gray-200" />
						</TableDataCell>
						<TableDataCell>
							<span className="inline-block h-4 w-12 animate-pulse rounded bg-gray-200" />
						</TableDataCell>
					</TableRowCell>
				))}
			</TableBody>
		</Table>
	)
}
